// "Keep the momentum" banner — after a call is scored it slides down softly from under the top frame,
// inviting the user to import more calls or jump to another lens. It lives at the app-region level (not
// inside a view), so it STAYS as the user moves between the group pages (Quick Snapshot / Cross-Skill /
// Buyer-Signals / MEDDPICC) and only leaves on an explicit dismiss:
//   • Close on the banner
//   • Import on the banner  (opens the usual picker)
//   • Import on a group page (main.ts wires the toolbar buttons to dismiss)
//   • a Cross-Skill / Buyer-Signals / MEDDPICC link on the banner (navigates there, then hides)
// It reappears after the NEXT scored call. On non-group pages it tucks away (without dismissing) and
// returns when the user comes back to a group page.
//
// Visibility preference ("Show Banner", default on) is a device UI pref in localStorage — same class as
// the trend-view toggle — so it survives sign-out / closing the app. The banner's "Don't show this
// again" checkbox turns it off; the Account › Preferences checkbox turns it back on (restores default).

import { navigateTo } from './sidenav.js';

const PREF_KEY = 'studio.momentumBanner.v1'; // '0' = off; anything else / absent = on (default)
const GROUP_VIEWS = new Set(['diagnostic', 'cross-skill', 'buyer-side', 'meddpicc']);

export function getBannerPref(): boolean {
  try {
    return localStorage.getItem(PREF_KEY) !== '0';
  } catch {
    return true;
  }
}
export function setBannerPref(on: boolean): void {
  try {
    localStorage.setItem(PREF_KEY, on ? '1' : '0');
  } catch {
    /* storage unavailable (private mode) — defaults to on each load */
  }
}

let overlay: HTMLElement | null = null;
let active = false; // a call has been scored and the banner should show on group pages
let currentView = '';
let openImportFn: () => void = () => {};
let showTimer: ReturnType<typeof setTimeout> | undefined;
const SHOW_DELAY_MS = 5000; // let the user take in the score first, then slide the banner down

interface InitOpts {
  openImport: () => void; // begins the usual import process (opens the file picker)
}

/** Mount the banner once as the first child of .app-region and wire its controls. */
export function initMomentumBanner(opts: InitOpts): void {
  openImportFn = opts.openImport;
  if (overlay) return;
  const region = document.querySelector('.app-region');
  if (!region) return;
  currentView = document.querySelector('.view.is-active')?.getAttribute('data-view') ?? '';
  overlay = build();
  region.insertBefore(overlay, region.firstChild);
}

function makeLink(label: string, action: string): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'momentum-link';
  b.dataset.mb = action;
  b.textContent = label;
  return b;
}

function build(): HTMLElement {
  const ov = document.createElement('div');
  ov.className = 'momentum-overlay';
  ov.id = 'momentum-overlay';
  ov.setAttribute('aria-hidden', 'true');

  const banner = document.createElement('div');
  banner.className = 'momentum-banner';
  banner.setAttribute('role', 'dialog');
  banner.setAttribute('aria-label', 'Keep the momentum');

  const h = document.createElement('h2');
  h.className = 'momentum-h';
  h.textContent = 'Let’s keep the momentum!';

  const lead = document.createElement('p');
  lead.className = 'momentum-lead';
  lead.append(makeLink('Import', '__import'), document.createTextNode(' more calls to reveal team or individual gaps'));

  // one line: the lead-in + the three lenses, comma-separated, to keep the banner short
  const links = document.createElement('p');
  links.className = 'momentum-links';
  links.append(
    document.createTextNode('Or explore: '),
    makeLink('Cross-Skill', 'cross-skill'),
    document.createTextNode(', '),
    makeLink('Buyer Signals', 'buyer-side'),
    document.createTextNode(', '),
    makeLink('MEDDPICC', 'meddpicc'),
  );

  const foot = document.createElement('div');
  foot.className = 'momentum-foot';
  const dismissLabel = document.createElement('label');
  dismissLabel.className = 'momentum-dismiss';
  const cb = document.createElement('input');
  cb.type = 'checkbox';
  cb.id = 'momentum-dont-show';
  dismissLabel.append(cb, document.createTextNode(' Don’t show this again'));
  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'momentum-close';
  close.dataset.mb = '__close';
  close.textContent = 'Close';
  foot.append(dismissLabel, close);

  banner.append(h, lead, links, foot);
  ov.appendChild(banner);

  ov.addEventListener('click', (e) => {
    const t = (e.target as HTMLElement).closest('[data-mb]') as HTMLElement | null;
    if (!t) return;
    const action = t.dataset.mb ?? '';
    if (action === '__close') {
      dismiss();
    } else if (action === '__import') {
      dismiss();
      openImportFn();
    } else {
      dismiss(); // a lens link: navigate there and hide until the next score
      navigateTo(action);
    }
  });
  cb.addEventListener('change', () => {
    if (!cb.checked) return;
    setBannerPref(false);
    const acct = document.getElementById('show-banner-optin') as HTMLInputElement | null;
    if (acct) acct.checked = false;
    dismiss();
  });

  return ov;
}

/** Score complete (call once per finished run): after a short delay, show the banner if the pref is on.
 *  The delay lets the score land first; a dismiss or a fresh run cancels the pending show. */
export function maybeShowMomentumBanner(): void {
  if (!getBannerPref()) return;
  clearTimeout(showTimer);
  showTimer = setTimeout(() => {
    active = true;
    apply();
  }, SHOW_DELAY_MS);
}

/** View changed: if the banner is showing and the user navigates elsewhere, slide it up (dismiss). */
export function onMomentumViewChange(view: string): void {
  const changed = view !== currentView;
  currentView = view;
  if (active && changed) {
    dismiss(); // navigating away slides the banner back up
    return;
  }
  apply();
}

/** Dismissed for this scored call — won't return until the next score. */
export function dismissMomentumBanner(): void {
  dismiss();
}

function dismiss(): void {
  clearTimeout(showTimer); // also cancels a still-pending delayed show (e.g. import during the 5s wait)
  active = false;
  apply();
  const cb = document.getElementById('momentum-dont-show') as HTMLInputElement | null;
  if (cb) cb.checked = false; // so a re-shown banner isn't pre-ticked
}

function apply(): void {
  if (!overlay) return;
  const open = active && GROUP_VIEWS.has(currentView);
  overlay.classList.toggle('is-open', open);
  overlay.setAttribute('aria-hidden', String(!open));
}
